import dayjs from 'dayjs';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEVERITY_COLOR, KIND_ICON, KIND_TYPE } from './replayEventMeta';
import { timelineDetail } from './replayTimelineDetail';
import { formatCoordinate } from '@/lib/coordinates';

/**
 * One event in the trip timeline: a severity-colored node (kind icon) on a
 * vertical connector, with a 3-line body — Type + time, the detail line, and
 * a muted location (POI name or coords). Clicking seeks the replay.
 */
export default function ReplayTimelineRow({ event, active, onSelect, isFirst, isLast }) {
  const { t } = useTranslation();
  const Icon = KIND_ICON[event.kind] || KIND_ICON.stop;
  const meta = KIND_TYPE[event.kind] || { labelKey: '', label: event.kind };
  const color = SEVERITY_COLOR[event.severity] || SEVERITY_COLOR.info;
  const loc = event.poiName || formatCoordinate(event.lat, event.lng, 'decimal');

  return (
    <button
      type="button"
      onClick={() => onSelect(event)}
      className={`relative w-full text-start flex gap-3 pe-2 py-2 rounded-xl transition-colors ${
        active ? 'bg-blue-500/[0.07]' : 'hover:bg-white/40 dark:hover:bg-slate-700/40'
      }`}
    >
      {/* connector line behind the node */}
      <span
        className="absolute w-0.5 bg-slate-200 dark:bg-slate-600"
        style={{ insetInlineStart: 17, top: isFirst ? 18 : 0, bottom: isLast ? 'calc(100% - 18px)' : 0 }}
      />
      <span
        className="relative z-[1] ms-1 w-7 h-7 rounded-full border-[3px] border-white flex items-center justify-center flex-shrink-0 shadow"
        style={{ background: color, boxShadow: active ? '0 0 0 3px rgba(37,99,235,0.45)' : undefined }}
      >
        <Icon size={14} color="#fff" strokeWidth={2.4} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-baseline gap-2">
          <span className="text-sm font-bold text-slate-800 dark:text-slate-100 tracking-tight truncate">
            {t(meta.labelKey, meta.label)}
          </span>
          <span className="ms-auto text-[11px] font-mono text-slate-400 tabular-nums flex-shrink-0">
            <bdi>{dayjs(event.tStart).format('HH:mm')}</bdi>
          </span>
        </span>
        <span className="block text-[13px] font-medium text-slate-600 dark:text-slate-300 mt-0.5 truncate">
          <bdi>{timelineDetail(event)}</bdi>
        </span>
        <span className="flex items-center gap-1 text-[11px] text-slate-400 mt-0.5 truncate">
          <MapPin size={11} className="flex-shrink-0" />
          <bdi>{loc}</bdi>
        </span>
      </span>
    </button>
  );
}
